type Consent = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
};

function injectScript({ src, id, async = true, defer = false }: { src: string; id?: string; async?: boolean; defer?: boolean }) {
  if (!src) return;
  if (id && document.getElementById(id)) return;
  const s = document.createElement("script");
  if (id) s.id = id;
  s.src = src;
  s.async = async;
  s.defer = defer;
  document.head.appendChild(s);
}

function injectInline(code: string, id?: string) {
  if (!code) return;
  if (id && document.getElementById(id)) return;
  const s = document.createElement("script");
  if (id) s.id = id;
  s.innerHTML = code;
  document.head.appendChild(s);
}

export function loadGTM(gtmId?: string) {
  if (!gtmId) return;
  if ((window as any).dataLayer) return;
  /* eslint-disable */
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  /* eslint-enable */
  injectScript({ src: `https://www.googletagmanager.com/gtm.js?id=${gtmId}`, id: `gtm-${gtmId}` });

  // noscript iframe for GTM (helpful for some setups)
  try {
    if (!document.getElementById(`gtm-ns-${gtmId}`)) {
      const nos = document.createElement("noscript");
      nos.id = `gtm-ns-${gtmId}`;
      nos.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
      document.body.insertBefore(nos, document.body.firstChild);
    }
  } catch (e) {
    // ignore
  }
}

export function loadGtag(measurementId?: string) {
  if (!measurementId) return;
  if ((window as any).gtagLoaded) return;
  injectScript({ src: `https://www.googletagmanager.com/gtag/js?id=${measurementId}`, id: `gtag-${measurementId}` });
  injectInline(
    `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', '${measurementId}');`,
    `gtag-init-${measurementId}`
  );
  (window as any).gtagLoaded = true;
}

export function loadClarity(clarityId?: string) {
  if (!clarityId) return;
  if ((window as any).clarityLoaded) return;
  injectInline(
    `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/${clarityId}";y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window, document, "clarity", "script", "${clarityId}");`,
    `clarity-${clarityId}`
  );
  (window as any).clarityLoaded = true;
}

export function loadMetaPixel(pixelId?: string) {
  if (!pixelId) return;
  if ((window as any).fbq) return;
  injectInline(
    `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod? n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script','https://connect.facebook.net/en_US/fbevents.js'); fbq('init', '${pixelId}'); fbq('track', 'PageView');`,
    `fb-pixel-${pixelId}`
  );
}

export function loadHubspot(hsId?: string) {
  if (!hsId) return;
  if ((window as any).hubspotLoaded) return;
  injectScript({ src: `https://js.hs-scripts.com/${hsId}.js`, id: `hs-${hsId}` });
  (window as any).hubspotLoaded = true;
}

export function loadCalcom() {
  if ((window as any).Cal && (window as any).Cal.loaded) return;
  injectScript({ src: "https://app.cal.com/embed/embed.js", id: "calcom-embed" });
}

export function initThirdPartyScripts(consent?: Consent | null) {
  // Initialize all configured third-party scripts regardless of user consent.
  // This intentionally ignores the `consent` parameter to allow unconditional loading.
  try {
    // eslint-disable-next-line no-console
    console.debug('[consent] initThirdPartyScripts called (unconditional)', consent ?? null);
  } catch {}

  const env = (window as any)?.__TCS_ENV__ ?? {};
  const GTM = (import.meta as any).env?.VITE_GTM_ID || env.VITE_GTM_ID || null;
  const GA = (import.meta as any).env?.VITE_GA_ID || env.VITE_GA_ID || null;
  const CLARITY = (import.meta as any).env?.VITE_CLARITY_ID || env.VITE_CLARITY_ID || null;
  const META = (import.meta as any).env?.VITE_META_PIXEL_ID || env.VITE_META_PIXEL_ID || null;
  const HUBSPOT = (import.meta as any).env?.VITE_HUBSPOT_ID || env.VITE_HUBSPOT_ID || null;
  const GADS = (import.meta as any).env?.VITE_GADS_ID || env.VITE_GADS_ID || null;

  try {
    // eslint-disable-next-line no-console
    console.debug('[consent] env ids', { GTM, GA, CLARITY, META, HUBSPOT, GADS });
  } catch {}

  // Load analytics and tracking scripts if configured
  if (GTM) loadGTM(GTM);
  if (GA) loadGtag(GA);
  if (CLARITY) loadClarity(CLARITY);
  if (GADS) loadGtag(GADS);

  // Load marketing pixels and embeds if configured
  if (META) loadMetaPixel(META);
  if (HUBSPOT) loadHubspot(HUBSPOT);
  loadCalcom();
}

export default {
  loadGTM,
  loadGtag,
  loadClarity,
  loadMetaPixel,
  loadHubspot,
  loadCalcom,
  initThirdPartyScripts,
};
